import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Volume2, Mic, Square, Play, Star } from "lucide-react";
import { play, sfx, stopAudio } from "../../lib/audio";
import { Ruby } from "./Ruby";

const MAX_MS = 7000;

export const Speaking = ({ activity, record, onDone }) => {
  const q = activity.question_data || {};
  const [state, setState] = useState("idle");
  const [clip, setClip] = useState(null);
  const [noMic, setNoMic] = useState(false);
  const mr = useRef(null);
  const timer = useRef(null);
  const alive = useRef(true);
  const finalize = useRef(null);
  const clipRef = useRef(null);
  clipRef.current = clip;
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
      if (clipRef.current) URL.revokeObjectURL(clipRef.current);
      clearTimeout(timer.current);
      if (mr.current?.state === "recording") mr.current.stop();
    };
  }, []);
  useEffect(() => { setTimeout(() => play(q.sentence, activity.audio_asset), 400); }, [activity]); // eslint-disable-line react-hooks/exhaustive-deps

  const stop = () => {
    clearTimeout(timer.current);
    const rec = mr.current;
    try { if (rec && rec.state !== "inactive") { rec.requestData?.(); rec.stop(); } } catch { /* already stopped */ }
    const fin = finalize.current;
    setTimeout(() => fin?.(), 600);
  };
  const start = async () => {
    stopAudio();
    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      setNoMic(true);
      setState("done");
      return;
    }
    const rec = new MediaRecorder(stream);
    const chunks = [];
    let ended = false;
    rec.ondataavailable = (e) => e.data.size && chunks.push(e.data);
    const end = () => {
      if (ended) return;
      ended = true;
      finalize.current = null;
      stream.getTracks().forEach((t) => t.stop());
      clearTimeout(timer.current);
      if (!alive.current) return;
      const url = chunks.length ? URL.createObjectURL(new Blob(chunks, { type: rec.mimeType || "audio/webm" })) : null;
      setClip((old) => { if (old) URL.revokeObjectURL(old); return url; });
      setState("done");
      if (url) new Audio(url).play().catch(() => {});
    };
    rec.onstop = end;
    finalize.current = end;
    mr.current = rec;
    rec.start(250);
    setState("recording");
    timer.current = setTimeout(stop, MAX_MS);
  };
  const finish = () => { sfx("good"); record(true); setTimeout(() => onDone(true), 600); };

  return (
    <div className="flex flex-col items-center gap-6 font-round" data-testid="game-speaking">
      <h2 className="text-3xl sm:text-4xl text-[#2c6e65]" data-testid="activity-instruction">跟我說</h2>
      <div className="text-[6rem] leading-none">{q.emoji}</div>
      <button data-testid="speaking-model-btn" onClick={() => play(q.sentence, activity.audio_asset)} className="bg-white rounded-[2rem] border-4 border-[#3E9C8F] px-8 py-5 flex items-center gap-3 shadow-lg">
        <Volume2 className="text-[#3E9C8F] shrink-0" size={32} />
        <Ruby data={q.ruby?.length ? q.ruby : [...(q.sentence || "")].map((c) => [c, ""])} className="text-4xl sm:text-5xl leading-[1.9] text-stone-900" testid="speaking-sentence" />
      </button>
      <div className="flex flex-wrap justify-center gap-4">
        {state === "recording" ? (
          <motion.button data-testid="speaking-stop-btn" onClick={() => { setState("stopping"); stop(); }} animate={{ scale: [1, 1.08, 1] }} transition={{ repeat: Infinity, duration: 1 }}
            className="w-28 h-28 rounded-full bg-red-500 text-white flex flex-col items-center justify-center shadow-xl"><Square size={36} fill="currentColor" /><span className="text-lg">說完了</span></motion.button>
        ) : (
          <button data-testid="speaking-record-btn" onClick={start} disabled={state === "stopping"} className="disabled:opacity-60 w-28 h-28 rounded-full bg-[#E8845C] text-white flex flex-col items-center justify-center shadow-xl">
            <Mic size={40} /><span className="text-lg">{state === "stopping" ? "聽聽看…" : state === "done" ? "再說一次" : "換我說"}</span>
          </button>
        )}
        {clip && state === "done" && (
          <button data-testid="speaking-playback-btn" onClick={() => new Audio(clip).play().catch(() => {})} className="w-28 h-28 rounded-full bg-white border-4 border-[#cfe8e2] text-[#2c6e65] flex flex-col items-center justify-center">
            <Play size={36} /><span className="text-lg">聽我說</span>
          </button>
        )}
      </div>
      {noMic && <p className="text-stone-500 text-lg" data-testid="speaking-no-mic">沒有麥克風也沒關係，跟著大聲說一次吧！</p>}
      {state === "done" && (
        <button data-testid="speaking-done-btn" onClick={finish} className="kid-btn px-10 bg-amber-400 text-[var(--ink)] text-2xl font-round flex items-center gap-2"><Star fill="currentColor" /> 我說好了</button>
      )}
    </div>
  );
};
